import { StateCreator } from 'zustand';
import { jwtDecode } from 'jwt-decode';
import { JwtPayload } from '@area-restrita/shared/authentication';
import {
  AppAbility,
  AuthtorizationHandler,
  defineAbilitiesFor,
  Rule,
} from '@area-restrita/shared/authorization';

export type AuthSession = JwtPayload & { accessToken: string };

interface AuthSessionState {
  session: AuthSession | null;
  pendingRedirect: string | null;
  getUserType: () => 'tenant' | 'company' | null;

  getAbility: () => AppAbility;

  checkAuthorization: (...handlers: AuthtorizationHandler[]) => boolean;
  signIn: (accessToken: string) => AuthSession;
  signOut: () => void;
  setPendingRedirect: (redirect: string | null) => void;
  consumePendingRedirect: () => string | null;
}

interface AuthSessionStoreFactory {
  onSignIn?: (session: AuthSession) => void;
  onSignOut?: (session: AuthSession) => void;
}

export const authSessionStoreFactory =
  ({
    onSignIn,
    onSignOut,
  }: AuthSessionStoreFactory): StateCreator<AuthSessionState> =>
  (set, get) => ({
    session: null,
    pendingRedirect: null,
    getUserType: () => {
      const { session } = get();

      return session?.organization?.company ? 'company' : 'tenant';
    },
    getAbility: () => {
      const { session } = get();

      return defineAbilitiesFor(
        (session?.organization?.rules as Rule[]) ?? [],
        session?.organization?.company ? 'company' : 'tenant',
      );
    },
    checkAuthorization: (...handlers) => {
      const { session, getAbility } = get();
      if (!session) return false;

      const hasPermission = handlers.length
        ? handlers.every((handler) => {
            if (typeof handler === 'function') return handler(getAbility());
            return handler.handle(getAbility());
          })
        : false;

      return hasPermission;
    },
    signIn: (accessToken) => {
      const payload = jwtDecode<JwtPayload>(accessToken);

      const session: AuthSession = {
        ...payload,
        accessToken,
      };

      onSignIn?.(session);
      set({ session });

      return session;
    },
    signOut: () => {
      const { session } = get();

      if (session) {
        onSignOut?.(session);
      }

      set({ session: null, pendingRedirect: null });
    },
    setPendingRedirect: (redirect) => {
      set({ pendingRedirect: redirect });
    },
    consumePendingRedirect: () => {
      const { pendingRedirect } = get();

      if (!pendingRedirect) {
        return null;
      }

      set({ pendingRedirect: null });

      return pendingRedirect;
    },
  });
